import { dashboardObservability } from "../utils/observability";
import { defineEvent as defineOperationalEvent } from "@diverge/observability/browser";
const operationalLogger = dashboardObservability.logger("dashboard/components/CriticalBugAlarm");
const operationalEvents = {
  event1: defineOperationalEvent({
    name: "dashboard.components.critical_bug_alarm.error_checking_for_new_critical_bugs.01686c98",
    message: "Error checking for new critical bugs:",
    level: "error",
    attributes: { "di.reason_code": { type: "category" } },
    classification: "operational",
    volume: "boundary",
    owner: "dashboard/src/components/CriticalBugAlarm",
  }),
};
import { useCallback, useEffect, useRef, useState } from "react";
import { SirenIcon, XIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useScope } from "../context/ScopeContext";
import { resolveScope } from "../context/scopeResolution";
import api from "../utils/api";
import { startIntervalPoller } from "../utils/intervalPoller";
import { playCriticalBugSiren } from "../utils/dashboardSounds";
import { isSolutionsTeam } from "../utils/staffTeam";

const POLL_INTERVAL_MS = 10_000;

function criticalCount(payload: unknown): number {
  const value = Number((payload as { critical_bug_count?: unknown })?.critical_bug_count ?? 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Alerts the **solutions team** when a critical bug arrives while this dashboard
 * session is already running. The first successful request is only a baseline: it
 * is never rendered or persisted, so old bugs cannot alarm on login or reload.
 *
 * A full-screen siren is the most interruptive thing in the dashboard, so it goes
 * only to the people who act on it. Everyone else — developers included — reads
 * critical bugs off the conversations list instead. Since `staffTeam` comes from a
 * realm role, an account without one is not on the solutions team and is not
 * alarmed: the polling stops too, so this is one fewer request every 10s for
 * every other session.
 */
export default function CriticalBugAlarm() {
  const auth = useAuth();
  const { tree } = useScope();
  // Incident response covers every accessible organization, independent of the
  // scope used to browse the dashboard. Scope changes must not reset the baseline.
  const { chatbotIds } = resolveScope(tree, { type: "all" });
  const [newBugCount, setNewBugCount] = useState(0);
  const baselineRef = useRef<number | null>(null);
  const chatbotIdsKey = chatbotIds.join(",");
  const hasChatbots = chatbotIds.length > 0;

  const dismiss = useCallback(() => setNewBugCount(0), []);

  useEffect(() => {
    baselineRef.current = null;
    setNewBugCount(0);

    if (
      !auth.isAuthenticated ||
      !auth.isAdmin ||
      !isSolutionsTeam(auth.staffTeam) ||
      !hasChatbots
    ) {
      return undefined;
    }

    const poller = startIntervalPoller({
      intervalMs: POLL_INTERVAL_MS,
      reason: "critical_bug_count_unavailable",
      // Do not establish a guessed baseline after a failed request. The first
      // successful snapshot remains silent, preventing buffered alarms.
      report: (error) =>
        operationalLogger.error(operationalEvents.event1, error, {
          "di.reason_code": "error_checking_for_new_critical_bugs",
        }),
      tick: async ({ signal }) => {
        const response = await api.get("/api/v1/conversations/critical-bug-count", {
          params: { chatbot_id: chatbotIdsKey },
        });
        if (signal.aborted) {
          return;
        }

        const nextCount = criticalCount(response.data);
        const previousCount = baselineRef.current;
        baselineRef.current = nextCount;

        // A missing baseline means this is the page's initial snapshot. Do not
        // replay bugs that predate the live dashboard session.
        if (previousCount === null || nextCount <= previousCount) {
          return;
        }

        setNewBugCount((current) => current + nextCount - previousCount);
        void playCriticalBugSiren();
      },
    });

    void poller.run();
    return () => poller.stop();
  }, [auth.isAdmin, auth.isAuthenticated, auth.staffTeam, chatbotIdsKey, hasChatbots]);

  if (newBugCount === 0) {
    return null;
  }

  return (
    <div
      aria-atomic="true"
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[100] flex animate-pulse items-center justify-center border-[18px] border-red-600 bg-red-600/55 p-6"
      data-testid="critical-bug-alarm"
      role="alert"
    >
      <div className="pointer-events-auto relative flex max-w-xl flex-col items-center rounded-3xl border-4 border-white bg-red-700 px-10 py-9 text-center text-white shadow-2xl">
        <button
          aria-label="Dismiss critical bug alarm"
          className="absolute top-3 right-3 rounded-full p-2 transition hover:bg-white/20"
          onClick={dismiss}
          type="button"
        >
          <XIcon className="size-6" />
        </button>
        <SirenIcon aria-hidden="true" className="mb-4 size-20" strokeWidth={2.5} />
        <h2 className="text-3xl font-black">Critical bug submitted</h2>
        <p className="mt-3 text-lg font-semibold">
          {newBugCount === 1
            ? "A new critical bug needs attention now."
            : `${newBugCount} new critical bugs need attention now.`}
        </p>
        <button
          className="mt-6 rounded-xl bg-white px-6 py-3 font-bold text-red-700 shadow-lg transition hover:bg-red-50"
          onClick={dismiss}
          type="button"
        >
          Stop alarm
        </button>
      </div>
    </div>
  );
}
